/**
 * 应用设置的持久化。
 *
 * ## 读取一律"逐字段校验 + 回退默认值"
 *
 * 记录可能来自旧版本程序、别的标签页、甚至同一版本的另一份构建。直接
 * `as AppSettings` 的结果是：升级后新增的字段读回来是 `undefined`，于是界面在
 * "主题为 undefined"这种状态下做出奇怪的事（最典型的是变成系统默认外观，而不是用户选过的外观），
 * 而且没有任何报错。逐字段校验的代价是几十行代码，收益是"升级后设置不会莫名其妙变样"。
 *
 * ## 并发写：最后写入胜出
 *
 * 两个标签页同时改设置时，这里不加锁、不自动合并，**最后写入胜出**。理由：设置是
 * "用户此刻的偏好"，两边的意图都是"就是现在这个值"，合并出一个谁都没选过的中间态更糟。
 * 覆盖行为是显式的（调用方拿到 `conflict`）。`save(patch)` 采用读改写、只覆盖补丁里出现的字段，
 * 这样"一个标签页改主题、另一个改单位"不会互相抹掉。
 */

import {
  NotStorableError,
  describeStorageFailure,
  failureError,
  runSave,
  saveOk,
  type SaveOutcome,
  type StorageFailure,
} from './errors';
import { RECORD_FORMAT, SETTINGS_KEY, isRecordLike, readBoolean, readFiniteNumber, readString } from './keys';
import type { KeyValueStore } from './store';

/**
 * 主题偏好。取值必须与 `@hgc/ui` 的 `ThemePreference` 完全一致。
 * core 不能依赖 ui（分层规则见 tools/check-boundaries.mjs），所以这里独立声明；
 * 改一边必须同步另一边，否则界面会把不认识的值当成"跟随系统"。
 */
export type ThemePreference = 'light' | 'dark' | 'system';

export type UnitSystem = 'metric' | 'imperial';

/**
 * 上次使用的链路配置。
 *
 * 形状与 `@hgc/gcs` 的 `LinkConfig` 保持一致（`replay` 除外）：
 * 回放配置里带着整份 `.tlog` 记录，把它当"上次使用的链路"存下来等于把几十兆遥测写进设置，
 * 下次打开还会复原一个指向已不存在文件的回放。因此 `replay` 被明确拒绝保存，
 * 而不是存一个残废形状让下次读取时炸掉。
 */
export type StoredLinkConfig =
  | {
      readonly kind: 'simulated';
      readonly count?: number;
      readonly aircraft?: 'copter' | 'plane';
      readonly tickMs?: number;
    }
  | { readonly kind: 'websocket'; readonly url: string; readonly protocols?: readonly string[] }
  | { readonly kind: 'serial'; readonly baudRate?: number }
  | { readonly kind: 'bluetooth' };

export interface AuditRetentionSettings {
  /** 审计日志的常规条数上限 */
  readonly maxEntries: number;
  /** 审计日志的估算字节上限 */
  readonly maxBytes: number;
}

export interface AppSettings {
  readonly theme: ThemePreference;
  readonly units: UnitSystem;
  /** 危险动作（解锁、电机测试、写参数）是否要二次确认 */
  readonly confirmDangerousCommands: boolean;
  /** 上次使用的链路配置；`null` 表示还没连过 */
  readonly lastLink: StoredLinkConfig | null;
  /** 审计日志容量，用户可以在设置里调小以腾出磁盘配额 */
  readonly audit: AuditRetentionSettings;
}

export const DEFAULT_APP_SETTINGS: AppSettings = Object.freeze({
  theme: 'system',
  units: 'metric',
  confirmDangerousCommands: true,
  lastLink: null,
  audit: { maxEntries: 2000, maxBytes: 2 * 1024 * 1024 },
});

export interface SettingsRecord {
  readonly format: number;
  readonly updatedAt: number;
  readonly settings: AppSettings;
}

function readTheme(value: unknown): ThemePreference | null {
  return value === 'light' || value === 'dark' || value === 'system' ? value : null;
}

function readUnits(value: unknown): UnitSystem | null {
  return value === 'metric' || value === 'imperial' ? value : null;
}

/** 链路配置校验；不认识或被明确排除的（`replay`）返回 null */
export function readStoredLinkConfig(value: unknown): StoredLinkConfig | null {
  if (!isRecordLike(value)) return null;
  const kind = readString(value['kind']);
  switch (kind) {
    case 'simulated': {
      const count = readFiniteNumber(value['count']);
      const tickMs = readFiniteNumber(value['tickMs']);
      const aircraft = readString(value['aircraft']);
      return {
        kind: 'simulated',
        ...(count === null ? {} : { count }),
        ...(tickMs === null ? {} : { tickMs }),
        ...(aircraft === 'copter' || aircraft === 'plane' ? { aircraft } : {}),
      };
    }
    case 'websocket': {
      const url = readString(value['url']);
      if (url === null || url.length === 0) return null;
      const protocols = Array.isArray(value['protocols'])
        ? value['protocols'].filter((entry): entry is string => typeof entry === 'string')
        : null;
      return {
        kind: 'websocket',
        url,
        ...(protocols === null || protocols.length === 0 ? {} : { protocols }),
      };
    }
    case 'serial': {
      const baudRate = readFiniteNumber(value['baudRate']);
      return { kind: 'serial', ...(baudRate === null ? {} : { baudRate }) };
    }
    case 'bluetooth':
      return { kind: 'bluetooth' };
    default:
      return null;
  }
}

function readAuditRetention(value: unknown, fallback: AuditRetentionSettings): AuditRetentionSettings {
  if (!isRecordLike(value)) return fallback;
  const maxEntries = readFiniteNumber(value['maxEntries']);
  const maxBytes = readFiniteNumber(value['maxBytes']);
  return {
    maxEntries: maxEntries !== null && maxEntries > 0 ? Math.floor(maxEntries) : fallback.maxEntries,
    maxBytes: maxBytes !== null && maxBytes > 0 ? Math.floor(maxBytes) : fallback.maxBytes,
  };
}

/**
 * 逐字段校验并补齐默认值。`fallback` 让调用方基于当前设置做增量回退
 * （某个字段坏了不至于把用户本次会话里刚改的值一起打回去）。
 */
export function readAppSettings(value: unknown, fallback: AppSettings = DEFAULT_APP_SETTINGS): AppSettings {
  if (!isRecordLike(value)) return fallback;
  return {
    theme: readTheme(value['theme']) ?? fallback.theme,
    units: readUnits(value['units']) ?? fallback.units,
    confirmDangerousCommands:
      readBoolean(value['confirmDangerousCommands']) ?? fallback.confirmDangerousCommands,
    lastLink:
      value['lastLink'] === null ? null : (readStoredLinkConfig(value['lastLink']) ?? fallback.lastLink),
    audit: readAuditRetention(value['audit'], fallback.audit),
  };
}

export interface SettingsRepositoryOptions {
  readonly now?: () => number;
  readonly onFailure?: (failure: StorageFailure) => void;
  /** 读取失败或字段缺失时用的默认值（默认 {@link DEFAULT_APP_SETTINGS}） */
  readonly defaults?: AppSettings;
}

export interface SettingsLoadResult {
  readonly settings: AppSettings;
  /**
   * 读取失败的原因；`null` 表示读成功（包括"还没有设置过"这种正常情况）。
   * 用返回值而不是"读失败就悄悄给默认值"：用户看到的现象一样（回到默认外观），
   * 但"设置被重置了"和"读不出来"是完全不同的两件事，界面要能说出是哪一种。
   */
  readonly failure: StorageFailure | null;
}

export interface SaveSettingsOptions {
  /** 调用方上次读到的 `updatedAt`。与库里不一致时仍然写入，但会回报 `conflict` */
  readonly expectedUpdatedAt?: number;
}

export class SettingsRepository {
  private readonly store: KeyValueStore;
  private readonly now: () => number;
  private readonly onFailure: ((failure: StorageFailure) => void) | undefined;
  private readonly defaults: AppSettings;

  constructor(store: KeyValueStore, options: SettingsRepositoryOptions = {}) {
    this.store = store;
    this.now = options.now ?? Date.now;
    this.onFailure = options.onFailure;
    this.defaults = options.defaults ?? DEFAULT_APP_SETTINGS;
  }

  async load(): Promise<SettingsLoadResult> {
    try {
      const raw = await this.store.get('settings', SETTINGS_KEY);
      if (raw === undefined) return { settings: this.defaults, failure: null };
      const nested = isRecordLike(raw) ? raw['settings'] : undefined;
      return { settings: readAppSettings(nested, this.defaults), failure: null };
    } catch (error) {
      const failure = describeStorageFailure(error, { operation: '读取设置', collection: 'settings' });
      this.onFailure?.(failure);
      return { settings: this.defaults, failure };
    }
  }

  /** 当前库里记录的 `updatedAt`；没有记录时返回 null（界面用来做乐观并发判断） */
  async storedUpdatedAt(): Promise<number | null> {
    const raw = await this.store.get('settings', SETTINGS_KEY);
    return isRecordLike(raw) ? readFiniteNumber(raw['updatedAt']) : null;
  }

  /** 保存设置补丁：只覆盖补丁里出现的字段 */
  async save(patch: Partial<AppSettings>, options: SaveSettingsOptions = {}): Promise<SaveOutcome<AppSettings>> {
    const outcome = await runSave(
      '保存设置',
      async () => {
        const raw = await this.store.get('settings', SETTINGS_KEY);
        const record = isRecordLike(raw) ? raw : null;
        const base = readAppSettings(record?.['settings'], this.defaults);
        const actualUpdatedAt = record === null ? null : readFiniteNumber(record['updatedAt']);

        const merged: AppSettings = {
          theme: readTheme(patch.theme) ?? base.theme,
          units: readUnits(patch.units) ?? base.units,
          confirmDangerousCommands:
            readBoolean(patch.confirmDangerousCommands) ?? base.confirmDangerousCommands,
          lastLink:
            patch.lastLink === undefined ? base.lastLink : readStoredLinkConfig(patch.lastLink),
          audit:
            patch.audit === undefined ? base.audit : readAuditRetention(patch.audit, base.audit),
        };

        const stored: SettingsRecord = {
          format: RECORD_FORMAT,
          updatedAt: this.now(),
          settings: merged,
        };
        await this.store.put('settings', SETTINGS_KEY, stored);

        const expected = options.expectedUpdatedAt;
        if (expected === undefined || actualUpdatedAt === expected) return saveOk(merged);
        return saveOk(merged, {
          expectedUpdatedAt: expected,
          actualUpdatedAt,
          message: `设置已被其它标签页更新（你读到的时间戳是 ${expected}，库里已是 ${actualUpdatedAt}），本次保存按「最后写入胜出」覆盖了对方的改动。`,
        });
      },
      { collection: 'settings' },
    );

    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }

  /**
   * 记住"上次使用的链路"。`replay` 会被明确拒绝（见 {@link StoredLinkConfig} 的注释）：
   * 返回可读失败，而不是存一个残废配置。
   */
  async saveLastLink(config: unknown): Promise<SaveOutcome<StoredLinkConfig | null>> {
    const outcome = await runSave(
      '保存上次使用的链路',
      async () => {
        if (config === null) {
          const saved = await this.save({ lastLink: null });
          if (!saved.ok) throw failureError(saved.failure);
          return saveOk(null as StoredLinkConfig | null);
        }
        if (isRecordLike(config) && config['kind'] === 'replay') {
          throw new NotStorableError(
            'settings/lastLink',
            '回放配置里带着整份遥测记录，不适合作为「上次使用的链路」保存',
          );
        }
        const normalized = readStoredLinkConfig(config);
        if (normalized === null) {
          const kind = isRecordLike(config) ? String(config['kind']) : typeof config;
          throw new NotStorableError('settings/lastLink', `无法识别的链路配置（kind=${kind}）`);
        }
        const saved = await this.save({ lastLink: normalized });
        if (!saved.ok) throw failureError(saved.failure);
        return saveOk(normalized);
      },
      { collection: 'settings' },
    );

    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }

  /** 恢复默认设置。界面上必须是明确的"重置"动作，不能是自动行为 */
  async reset(): Promise<SaveOutcome<AppSettings>> {
    const outcome = await runSave(
      '重置设置',
      async () => {
        const stored: SettingsRecord = {
          format: RECORD_FORMAT,
          updatedAt: this.now(),
          settings: this.defaults,
        };
        await this.store.put('settings', SETTINGS_KEY, stored);
        return saveOk(this.defaults);
      },
      { collection: 'settings' },
    );
    if (!outcome.ok) this.onFailure?.(outcome.failure);
    return outcome;
  }
}
